import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { formatMeasurement } from '@klareospace/pharos-lib-front-utils';
import { WEIGHT_UNITS, type Order, type WeightUnit } from '@klareospace/pharos-lib-types';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';

import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import { ComputableValueCell } from './computable-value-cell';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WeightInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly isError: boolean;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

type WeightDraft = { unit: WeightUnit; value: number };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidWeight(draft: WeightDraft): boolean {
  return Number.isFinite(draft.value) && draft.value > 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WeightInlineEditor({
  fieldState,
  isError,
  lockReason,
  order,
}: WeightInlineEditorProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const current = order.totalWeight?.data ?? { unit: WEIGHT_UNITS.KG, value: 0 };

  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit<WeightDraft>({
      buildPatch: (value) => ({ totalWeight: value }),
      isValid: isValidWeight,
      order,
      successKey: 'drawer.edit.content.success',
      value: current,
    });

  if (isEditing) {
    return (
      <div className="mt-1 flex items-center gap-1">
        <Input
          className="h-7 flex-1 text-sm"
          disabled={isPending}
          min={0}
          onChange={(e): void => setDraft({ ...draft, value: Number(e.target.value) })}
          step="0.01"
          type="number"
          value={draft.value}
        />
        <Select
          disabled={isPending}
          onValueChange={(unit): void => setDraft({ ...draft, unit: unit as WeightUnit })}
          value={draft.unit}
        >
          <SelectTrigger className="h-7 w-16 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(WEIGHT_UNITS).map((unit) => (
              <SelectItem key={unit} value={unit}>
                {t(`units.${unit}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineEditActions isPending={isPending} isValid={isValid} onCancel={handleCancel} onConfirm={handleConfirm} />
      </div>
    );
  }

  const weightResult = formatMeasurement(order.totalWeight, (unit) => t(`units.${unit}`));
  const formatted = weightResult ? weightResult.text : '—';

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      <ComputableValueCell formatted={formatted} isComputed={order.totalWeight?.isComputed} isError={isError} />
    </EditableFieldWrapper>
  );
}
